import FORMATS from '../constants/format-nombres.json';

export let DEVISE = 'CHF';

// Espace insécable : un montant ne se coupe jamais en fin de ligne.
const ESPACE = '\u00a0';

/**
 * Séparateurs des milliers et des décimales d'un pays, selon la table
 * `constants/format-nombres.json` que les PDF du serveur lisent aussi :
 * 3'675.40 en Suisse, 3 675,40 en France, 3.675,40 en Allemagne, 3,675.40
 * ailleurs. Une table plutôt qu'une locale du navigateur : ce qui figure sur
 * une facture ne doit pas varier d'un appareil à l'autre.
 *
 * @param {string} [pays] code du pays de la société
 * @returns {{milliers: string, decimales: string}}
 */
export function separateursDuPays(pays) {
  const code = (pays || 'CH').toUpperCase();
  if (FORMATS.apostrophePoint.includes(code)) return { milliers: "'", decimales: '.' };
  if (FORMATS.pointVirgule.includes(code)) return { milliers: '.', decimales: ',' };
  if (FORMATS.espaceVirgule.includes(code)) return { milliers: ESPACE, decimales: ',' };
  return { milliers: ',', decimales: '.' };
}

let SEPARATEURS = separateursDuPays('CH');

/** Les montants s'écrivent désormais à la façon du pays de la société. */
export const setPaysMontants = (pays) => {
  SEPARATEURS = separateursDuPays(pays);
};

/** Le séparateur décimal du pays courant : « . » en Suisse, « , » en Allemagne. */
export const decimalesDuPays = () => SEPARATEURS.decimales;

export const setDeviseGlobale = (d) => {
  DEVISE = d || 'CHF';
};

/**
 * Écrit un montant à la façon du pays de la société ; en Suisse, à la forme
 * suisse : apostrophe pour les milliers, point pour les décimales — `3'675.40`.
 *
 * La forme française — espace fine et virgule — se lit mal sur un bulletin de
 * versement et n'est pas celle que les guichets attendent ; la spécification de
 * la QR-facture impose celle-ci. Les documents PDF produits par le serveur
 * l'emploient déjà : l'écran s'y aligne, faute de quoi un même montant ne
 * s'écrirait pas de la même façon à l'écran et sur la pièce envoyée au client.
 *
 * Le découpage est fait ici plutôt que laissé à une locale : `fr-CH` rend
 * `3 675,40`, et la locale qui rend la bonne forme dépend de la version des
 * données ICU embarquées par le navigateur. Ce qui figure sur une facture ne
 * doit pas varier d'un appareil à l'autre.
 *
 * @param {number|string} n montant à écrire
 * @returns {string} le montant écrit
 */
export const fmt = (n) => {
  const valeur = Number(n) || 0;
  const [entier, decimales] = Math.abs(valeur).toFixed(2).split('.');
  const milliers = entier.replace(/\B(?=(\d{3})+(?!\d))/g, SEPARATEURS.milliers);
  const montant = `${valeur < 0 ? '-' : ''}${milliers}${SEPARATEURS.decimales}${decimales}`;
  // En arabe, en hébreu… l'apostrophe des milliers coupait le nombre en deux
  // (« 932.95'100 ») : le montant s'isole, il se lit de gauche à droite.
  return ecritureDeDroiteAGauche() ? '\u2066' + montant + '\u2069' : montant;
};

/** Vrai quand l'interface s'écrit de droite à gauche (arabe, hébreu, persan, ourdou). */
function ecritureDeDroiteAGauche() {
  return typeof document !== 'undefined' && document.documentElement?.dir === 'rtl';
}

export const fmtH = (h) => `${Math.floor(h)}h${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;
